import { useEffect, useRef } from 'react';
import { Geolocation } from '@capacitor/geolocation';
import api from '@/lib/api';
import { getNetworkState, onReconnect } from '@/lib/network/connectivity';
import { isNetworkFailure } from '@/lib/network/apiErrors';

const HEARTBEAT_MS = 10000;

/**
 * Send provider GPS to backend while availability is "available" or "busy" (en route).
 * Stops when offline or unassigned. Does not queue backlog on reconnect.
 */
export default function useProviderLocationHeartbeat(availabilityStatus) {
  const timerRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (availabilityStatus !== 'available' && availabilityStatus !== 'busy') {
      return undefined;
    }

    const sendLocation = async () => {
      if (!getNetworkState().isOnline || sendingRef.current) {
        return;
      }
      sendingRef.current = true;
      try {
        const perm = await Geolocation.requestPermissions();
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
          return;
        }
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000,
        });
        await api.patch('/providers/me/location', {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          source: 'gps',
        });
      } catch (e) {
        if (!isNetworkFailure(e)) {
          // Throttled or permission denied — silent on heartbeat
        }
      } finally {
        sendingRef.current = false;
      }
    };

    sendLocation();
    timerRef.current = setInterval(sendLocation, HEARTBEAT_MS);

    const unsubReconnect = onReconnect(() => {
      sendLocation();
    });

    return () => {
      unsubReconnect();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [availabilityStatus]);
}
